// 小さな DOM ヘルパ（UI フレームワークなし）。

type Props = Record<string, unknown>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === "class") node.className = String(v);
    else if (k === "text") node.textContent = String(v);
    // ※ innerHTML 相当のプロパティは意図的に用意しない。
    //   アバター名やシェイプキー名は .fmavatar 由来の非信頼文字列なので、
    //   必ず textContent 経由で描画して XSS を構造的に防ぐ。
    else if (k === "style" && typeof v === "object") Object.assign(node.style, v);
    else if (k.startsWith("on") && typeof v === "function") {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "value" && node instanceof HTMLInputElement) node.value = String(v);
    else if (v === true) node.setAttribute(k, "");
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

const ICON_PATHS: Record<string, string> = {
  avatar: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/>',
  expression:
    '<circle cx="12" cy="12" r="9"/><path d="M8 14c1 1.5 2.4 2.3 4 2.3s3-.8 4-2.3"/><path d="M8.5 9.5h.01M15.5 9.5h.01"/>',
  gesture:
    '<path d="M8 13V6.5a1.5 1.5 0 0 1 3 0V12M11 12V4.5a1.5 1.5 0 0 1 3 0V12M14 12V6.5a1.5 1.5 0 0 1 3 0V13"/><path d="M17 13V9.5a1.5 1.5 0 0 1 3 0V15c0 3.9-3.1 7-7 7h-1c-2.5 0-4-.9-5.5-2.7L4 16.8c-.6-.8-.4-1.8.4-2.3.7-.5 1.6-.3 2.2.3L8 16"/>',
  export: '<path d="M12 16V4M7 9l5-5 5 5"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/>',
  folder: '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/>',
};

/** モノクロ線画アイコン。fill なし・stroke=currentColor で親要素の color を継承する */
export function icon(name: keyof typeof ICON_PATHS): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = ICON_PATHS[name] ?? "";
  return svg;
}

export function clear(node: HTMLElement): HTMLElement {
  while (node.firstChild) node.removeChild(node.firstChild);
  return node;
}

/**
 * 中央モーダル。close() で閉じる。
 * onClose は閉じ方（close() 呼び出し・✕ ボタン・背景クリック）に関わらず、閉じたときに1回だけ呼ばれる
 * （カメラ停止・通信の中断などの後始末に使う）。
 */
export function modal(
  title: string,
  body: HTMLElement,
  actions: HTMLElement[] = [],
  onClose?: () => void,
): { close: () => void } {
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    backdrop.remove();
    onClose?.();
  };
  const backdrop = el(
    "div",
    {
      class: "modal-backdrop",
      onclick: (e: MouseEvent) => {
        if (e.target === backdrop) close();
      },
    },
    el(
      "div",
      { class: "modal", role: "dialog", "aria-modal": "true" },
      el(
        "div",
        { class: "modal-head" },
        el("h2", { text: title }),
        el("button", { class: "icon-btn", "aria-label": "閉じる", text: "✕", onclick: close }),
      ),
      el("div", { class: "modal-body" }, body),
      actions.length ? el("div", { class: "modal-actions" }, ...actions) : null,
    ),
  );
  document.body.appendChild(backdrop);
  return { close };
}

/** 文字列入力を求めるプロンプト。OK で resolve、キャンセルで null。hint を渡すと入力欄の下に注意文を表示する。 */
export function promptText(
  title: string,
  label: string,
  initial: string,
  validate?: (v: string) => string | null,
  hint?: string,
): Promise<string | null> {
  return new Promise((resolve) => {
    const input = el("input", { class: "text-input", type: "text", value: initial, maxlength: "255" });
    const err = el("p", { class: "field-error" });
    let done = false;
    const finish = (v: string | null): void => {
      if (done) return;
      done = true;
      handle.close();
      resolve(v);
    };
    const ok = (): void => {
      const v = input.value.trim();
      const msg = validate ? validate(v) : null;
      if (msg) {
        err.textContent = msg;
        return;
      }
      finish(v);
    };
    input.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") ok();
    });
    const handle = modal(
      title,
      el(
        "div",
        { class: "field" },
        el("label", { text: label }),
        input,
        hint ? el("p", { class: "muted", text: hint }) : null,
        err,
      ),
      [
        el("button", { class: "btn ghost", text: "キャンセル", onclick: () => finish(null) }),
        el("button", { class: "btn primary", text: "OK", onclick: ok }),
      ],
      () => finish(null),
    );
    setTimeout(() => {
      input.focus();
      input.select();
    }, 30);
  });
}

/** はい/いいえ確認 */
export function confirmDialog(title: string, message: string, okLabel = "OK"): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: boolean): void => {
      if (done) return;
      done = true;
      handle.close();
      resolve(v);
    };
    const handle = modal(title, el("p", { class: "muted", text: message }), [
      el("button", { class: "btn ghost", text: "キャンセル", onclick: () => finish(false) }),
      el("button", { class: "btn danger", text: okLabel, onclick: () => finish(true) }),
    ], () => finish(false));
  });
}

export interface MenuItem {
  label: string;
  value: string;
  danger?: boolean;
}

/**
 * 「⋮」ボタン等から開くコンテキストメニュー。
 * anchor には基準要素、またはクリック座標を渡す。
 * @returns 選ばれた value。背景クリックでキャンセルすると null。
 */
export function openMenu(items: MenuItem[], anchor: HTMLElement | { x: number; y: number }): Promise<string | null> {
  return new Promise((resolve) => {
    const close = (v: string | null): void => {
      back.remove();
      menu.remove();
      resolve(v);
    };
    const back = el("div", { class: "menu-back", onclick: () => close(null) });
    const menu = el(
      "div",
      { class: "menu", style: { visibility: "hidden" } },
      ...items.map((it) =>
        el("button", { class: it.danger ? "danger" : "", text: it.label, onclick: () => close(it.value) }),
      ),
    );
    document.body.append(back, menu);

    const rect = anchor instanceof HTMLElement ? anchor.getBoundingClientRect() : null;
    const px = rect ? rect.right : (anchor as { x: number; y: number }).x;
    const py = rect ? rect.bottom : (anchor as { x: number; y: number }).y;
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = px;
    let top = py;
    if (left + mw > vw - 8) left = Math.max(8, rect ? rect.left - mw : px - mw);
    if (top + mh > vh - 8) top = Math.max(8, py - mh);
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    menu.style.visibility = "visible";
  });
}

export function downloadBytes(bytes: Uint8Array, filename: string, mime = "application/zip"): void {
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = el("a", { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * localStorage の安全な読み出し。プライベートブラウジング・ストレージ無効化・容量超過などで
 * 例外が出ても null を返して処理を継続する。
 */
export function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** localStorage の安全な書き込み。失敗しても例外を投げない（保存されないだけ）。 */
export function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 保存できなくても今回のセッションの動作には影響しない */
  }
}
